import {
  ArrowDown,
  ArrowUpRight,
  Braces,
  Check,
  ChevronDown,
  CircleAlert,
  Github,
  Languages,
  RefreshCw,
  Scale,
  Sparkles,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { Language, Results, StrategySummary } from './types'

const strategyOrder = ['length', 'holistic', 'rubric', 'rubric_selective']
const strategyShort: Record<string, string> = {
  length: 'Length',
  holistic: 'Holistic',
  rubric: 'Rubric',
  rubric_selective: 'Selective',
}

const strategyDescriptions: Record<string, string> = {
  length: 'Always chooses the longer answer. A transparent weak baseline.',
  holistic: 'One direct quality judgment with no dimension-level scoring.',
  rubric: 'Scores five support-quality dimensions before choosing.',
  rubric_selective: 'Keeps only rubric choices that survive an A/B order reversal.',
}

const sourceLabels: Record<string, string> = {
  policy_reference: 'Controlled reference',
  human_preference: 'Human preference',
  human_preference_transferred: 'Translated preference',
}

const glossaryTerms = [
  {
    term: 'LLM judge',
    definition: 'An AI model that compares two answers and decides which one is better.',
  },
  {
    term: 'Golden / reference set',
    definition: 'A test set where the preferred answer is recorded before the judge is evaluated.',
  },
  {
    term: 'Reference agreement',
    definition: 'How often the judge picks the same answer as the recorded reference.',
  },
  {
    term: 'Coverage',
    definition: 'The share of cases where the judge gives an answer instead of abstaining.',
  },
  {
    term: 'A/B swap & order flip',
    definition: 'I reverse the answer order; an order flip means the judge then changes its choice.',
  },
  {
    term: 'Abstention',
    definition: 'A deliberate “no decision” when the result is unstable after the order check.',
  },
  {
    term: 'Confidence interval (CI)',
    definition: 'A range that shows uncertainty around a score. Smaller datasets give wider ranges.',
  },
  {
    term: 'Provenance',
    definition: 'A record of where the data came from and what was translated or changed.',
  },
]

const pct = (value: number | null, digits = 0) =>
  value === null ? '—' : `${(value * 100).toFixed(digits)}%`

const compactModel = (model: string | null) => model?.split('/').at(-1) ?? 'Not recorded'

function MetricInfo({ label, text }: { label: string; text: string }) {
  return (
    <span className="metric-info" title={text}>
      {label}
      <span aria-hidden="true">?</span>
    </span>
  )
}

function StrategyCard({ item, featured }: { item: StrategySummary; featured: boolean }) {
  return (
    <article className={`strategy-card ${featured ? 'featured' : ''}`}>
      <div className="strategy-card-top">
        <span className="strategy-index">0{strategyOrder.indexOf(item.strategy) + 1}</span>
        {featured && <span className="recommended-badge">Reliability gate</span>}
      </div>
      <h3>{item.label}</h3>
      <p>{strategyDescriptions[item.strategy]}</p>
      <div className="strategy-primary">
        <strong>{pct(item.agreement)}</strong>
        <span>reference agreement</span>
      </div>
      <div className="ci-line">
        <span>95% CI</span>
        <span>
          {item.agreement_ci95
            ? `${pct(item.agreement_ci95[0])}–${pct(item.agreement_ci95[1])}`
            : '—'}
        </span>
      </div>
      {item.parser_repairs > 0 && (
        <div
          className="parser-note"
          title="The raw model output required documented parser normalization but remains included in the artifact."
        >
          <Braces size={12} /> {item.parser_repairs}/{item.model_outputs} outputs normalized
        </div>
      )}
      <div className="strategy-secondary">
        <div>
          <span>Coverage</span>
          <strong>{pct(item.coverage)}</strong>
        </div>
        <div>
          <span>{item.strategy === 'rubric_selective' ? 'E2E accuracy' : 'Order flips'}</span>
          <strong>
            {item.strategy === 'rubric_selective'
              ? pct(item.end_to_end_accuracy)
              : pct(item.order_flip_rate)}
          </strong>
        </div>
      </div>
    </article>
  )
}

function ReliabilityChart({ strategies }: { strategies: StrategySummary[] }) {
  const plot = strategies.map((strategy, index) => {
    const coverage = strategy.coverage ?? 0
    const agreement = strategy.agreement ?? 0
    return {
      ...strategy,
      x: 76 + coverage * 490,
      y: 238 - Math.max(0, Math.min(1, (agreement - 0.4) / 0.6)) * 180,
      index,
    }
  })

  return (
    <div className="chart-wrap">
      <svg
        className="reliability-chart"
        viewBox="0 0 640 300"
        role="img"
        aria-label="Reference agreement plotted against coverage for each strategy"
      >
        <defs>
          <linearGradient id="frontier" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#587b23" stopOpacity="0.12" />
            <stop offset="1" stopColor="#d1f470" stopOpacity="0.38" />
          </linearGradient>
        </defs>
        {[0.4, 0.6, 0.8, 1].map((tick) => {
          const y = 238 - ((tick - 0.4) / 0.6) * 180
          return (
            <g key={tick}>
              <line x1="76" x2="582" y1={y} y2={y} className="chart-grid" />
              <text x="58" y={y + 4} textAnchor="end" className="chart-tick">
                {Math.round(tick * 100)}%
              </text>
            </g>
          )
        })}
        {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
          const x = 76 + tick * 490
          return (
            <g key={tick}>
              <line x1={x} x2={x} y1="58" y2="238" className="chart-grid" />
              <text x={x} y="260" textAnchor="middle" className="chart-tick">
                {Math.round(tick * 100)}%
              </text>
            </g>
          )
        })}
        <path d="M76 238 C240 220, 420 130, 566 58 L566 238 Z" fill="url(#frontier)" />
        <text x="16" y="36" className="chart-axis-title">
          AGREEMENT
        </text>
        <text x="582" y="287" textAnchor="end" className="chart-axis-title">
          COVERAGE
        </text>
        <text x="428" y="78" className="chart-frontier-label">
          more agreement + coverage →
        </text>
        {plot.map((point) => {
          const alignRight = point.x > 500
          const labelY = point.y + (point.index % 2 === 0 ? -16 : 26)
          return (
            <g key={point.strategy} className={`chart-point point-${point.strategy}`}>
              <circle cx={point.x} cy={point.y} r="13" />
              <circle cx={point.x} cy={point.y} r="4" className="chart-point-core" />
              <text
                x={point.x + (alignRight ? -20 : 20)}
                y={labelY}
                textAnchor={alignRight ? 'end' : 'start'}
                className="chart-point-label"
              >
                {strategyShort[point.strategy]}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

function BreakdownBars({ data, strategy }: { data: Results; strategy: string }) {
  const language = data.breakdowns.filter(
    (row) => row.strategy === strategy && row.dimension === 'language',
  )
  const consistency = data.language_consistency.find((row) => row.strategy === strategy)

  return (
    <div className="parity-grid" data-reveal>
      <div className="parity-bars">
        {language.map((row) => (
          <div className="bar-row" key={row.slice}>
            <div className="bar-label">
              <span>{row.slice === 'pt-PT' ? 'Português (PT)' : 'English'}</span>
              <strong>{pct(row.agreement)}</strong>
            </div>
            <div className="bar-track">
              <span style={{ width: pct(row.agreement) }} />
            </div>
            <small>
              {row.answered}/{row.n} judgments · CI{' '}
              {row.agreement_ci95
                ? `${pct(row.agreement_ci95[0])}–${pct(row.agreement_ci95[1])}`
                : '—'}
            </small>
          </div>
        ))}
      </div>
      <div className="consistency-card">
        <Languages size={22} />
        <span>Cross-language consistency</span>
        <strong>{pct(consistency?.consistency ?? null)}</strong>
        <p>
          Same normalized choice in {consistency?.comparable_groups ?? 0} comparable bilingual
          pairs.
        </p>
      </div>
    </div>
  )
}

function StressTest({ base, stress }: { base: Results; stress: Results }) {
  const rows = strategyOrder.map((strategy) => {
    const before = base.strategies.find((item) => item.strategy === strategy)
    const after = stress.strategies.find((item) => item.strategy === strategy)
    return { strategy, before, after }
  })
  const length = rows.find((row) => row.strategy === 'length')

  return (
    <div className="stress-grid" data-reveal>
      <article className="stress-story">
        <span className="tradeoff-kicker">LENGTH BASELINE / STRESS TEST</span>
        <h3>
          Length baseline: {pct(length?.before?.end_to_end_accuracy ?? null)} →{' '}
          {pct(length?.after?.end_to_end_accuracy ?? null)} after padding the losing answer
        </h3>
        <p>
          Every preferred answer in the reference set was at least as long as its alternative.
          The length-only rule therefore reached {pct(length?.before?.agreement ?? null)} agreement
          on that set.
        </p>
        <div className="stress-number">
          <div><span>Golden set</span><strong>{pct(length?.before?.end_to_end_accuracy ?? null)}</strong></div>
          <ArrowDown />
          <div><span>Padded loser</span><strong>{pct(length?.after?.end_to_end_accuracy ?? null)}</strong></div>
        </div>
        <p className="stress-disclosure">
          I generated this label-informed perturbation after detecting the confound. It is a
          targeted robustness test, not an independent benchmark.
        </p>
      </article>
      <article className="stress-comparison">
        <div className="card-heading">
          <div><span>Verbosity perturbation</span><h3>End-to-end accuracy</h3></div>
          <MetricInfo label="Why E2E?" text="Correct choices divided by all cases, so selective abstentions are not hidden." />
        </div>
        <div className="stress-legend"><span>Golden set</span><span>Padded losing answer</span></div>
        <div className="stress-rows">
          {rows.map(({ strategy, before, after }) => {
            const baseValue = before?.end_to_end_accuracy ?? 0
            const stressValue = after?.end_to_end_accuracy ?? 0
            const delta = stressValue - baseValue
            return (
              <div className="stress-row" key={strategy}>
                <strong>{strategyShort[strategy]}</strong>
                <div className="dual-bars">
                  <span className="base-bar" style={{ width: pct(baseValue) }} />
                  <span className="stress-bar" style={{ width: pct(stressValue) }} />
                </div>
                <div className="stress-values"><span>{pct(baseValue)}</span><span>{pct(stressValue)}</span></div>
                <span className={`delta ${delta >= 0 ? 'positive' : 'negative'}`}>
                  {delta > 0 ? '+' : ''}{pct(delta, 1)}
                </span>
              </div>
            )
          })}
        </div>
      </article>
    </div>
  )
}

function Candidate({ label, text, preferred }: { label: string; text: string; preferred: boolean }) {
  return (
    <div className={`candidate ${preferred ? 'preferred' : ''}`}>
      <div className="candidate-head">
        <span>Response {label}</span>
        {preferred && (
          <span className="reference-badge">
            <Check size={13} /> Reference
          </span>
        )}
      </div>
      <p>{text}</p>
    </div>
  )
}

function CaseExplorer({ data, stress }: { data: Results; stress: Results }) {
  const [dataset, setDataset] = useState<'golden' | 'stress'>('golden')
  const [strategy, setStrategy] = useState('rubric_selective')
  const [language, setLanguage] = useState<'all' | Language>('all')
  const [source, setSource] = useState('all')
  const [risk, setRisk] = useState('all')
  const [selectedId, setSelectedId] = useState(data.cases[0]?.id ?? '')

  const activeData = dataset === 'golden' ? data : stress
  const filtered = useMemo(
    () =>
      activeData.cases.filter(
        (item) =>
          (language === 'all' || item.language === language) &&
          (source === 'all' || item.source_kind === source) &&
          (risk === 'all' || item.risk === risk),
      ),
    [activeData.cases, language, risk, source],
  )
  const selected = filtered.find((item) => item.id === selectedId) ?? filtered[0]

  if (!selected) return null
  const prediction = selected.predictions[strategy]

  return (
    <div className="explorer-shell" data-reveal>
      <div className="explorer-toolbar">
        <label>
          <span>Dataset</span>
          <select value={dataset} onChange={(event) => setDataset(event.target.value as 'golden' | 'stress')}>
            <option value="golden">Golden set</option>
            <option value="stress">Verbosity stress</option>
          </select>
          <ChevronDown aria-hidden="true" />
        </label>
        <label>
          <span>Judge</span>
          <select value={strategy} onChange={(event) => setStrategy(event.target.value)}>
            {strategyOrder.map((item) => (
              <option value={item} key={item}>
                {strategyShort[item]}
              </option>
            ))}
          </select>
          <ChevronDown aria-hidden="true" />
        </label>
        <label>
          <span>Language</span>
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value as 'all' | Language)}
          >
            <option value="all">All</option>
            <option value="en">English</option>
            <option value="pt-PT">Português (PT)</option>
          </select>
          <ChevronDown aria-hidden="true" />
        </label>
        <label>
          <span>Reference</span>
          <select value={source} onChange={(event) => setSource(event.target.value)}>
            <option value="all">All</option>
            <option value="policy_reference">Controlled</option>
            <option value="human_preference">Human</option>
            <option value="human_preference_transferred">Translated</option>
          </select>
          <ChevronDown aria-hidden="true" />
        </label>
        <label>
          <span>Risk</span>
          <select value={risk} onChange={(event) => setRisk(event.target.value)}>
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <ChevronDown aria-hidden="true" />
        </label>
      </div>

      <div className="explorer-layout">
        <aside className="case-list" aria-label="Evaluation cases">
          <div className="case-list-count">{filtered.length} cases</div>
          {filtered.map((item) => {
            const itemPrediction = item.predictions[strategy]
            return (
              <button
                type="button"
                className={item.id === selected.id ? 'active' : ''}
                key={item.id}
                onClick={() => setSelectedId(item.id)}
              >
                <span className={`result-dot ${itemPrediction.correct ? 'correct' : 'wrong'}`}>
                  {itemPrediction.abstained ? '–' : itemPrediction.correct ? '✓' : '×'}
                </span>
                <span className="case-list-text">
                  <strong>{item.prompt}</strong>
                  <small>
                    {item.language} · {item.primary_dimension}
                  </small>
                </span>
              </button>
            )
          })}
        </aside>

        <article className="case-detail">
          <div className="case-meta">
            <span>{selected.id}</span>
            <span className={`risk-pill risk-${selected.risk}`}>{selected.risk} risk</span>
            <span>{sourceLabels[selected.source_kind]}</span>
          </div>
          <div className="customer-prompt">
            <span>Customer</span>
            <h3>{selected.prompt}</h3>
          </div>
          <div className="candidate-grid">
            <Candidate label="A" text={selected.response_a} preferred={selected.preferred === 'A'} />
            <Candidate label="B" text={selected.response_b} preferred={selected.preferred === 'B'} />
          </div>

          <div className={`judge-verdict ${prediction.correct ? 'correct' : 'incorrect'}`}>
            <div className="verdict-icon">
              {prediction.abstained ? (
                <Scale size={22} />
              ) : prediction.correct ? (
                <Check size={22} />
              ) : (
                <X size={22} />
              )}
            </div>
            <div>
              <span>{strategyShort[strategy]} judge</span>
              <h4>
                {prediction.abstained
                  ? 'Abstained after the swap test'
                  : `Chose response ${prediction.choice} — ${prediction.correct ? 'matched' : 'missed'} the reference`}
              </h4>
              <p>{prediction.rationale}</p>
            </div>
            <div className="order-check">
              <RefreshCw size={14} />
              <span>Swap test</span>
              <strong>
                {prediction.order_stable === null
                  ? 'n/a'
                  : prediction.order_stable
                    ? 'stable'
                    : 'flipped'}
              </strong>
            </div>
          </div>

          <details className="reference-note">
            <summary>Why this is the reference <ChevronDown size={15} /></summary>
            <p>{selected.reference_rationale}</p>
            {selected.source && (
              <p className="provenance">
                {selected.source.dataset} · row {selected.source.row} · votes{' '}
                {selected.source.annotator_votes.join(', ')} · {selected.source.license}
              </p>
            )}
          </details>
        </article>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <main className="state-page">
      <div className="brand-mark">JS</div>
      <RefreshCw className="spin" />
      <h1>Loading the experiment</h1>
      <p>Reading the committed evaluation artifact…</p>
    </main>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <main className="state-page">
      <div className="brand-mark">JS</div>
      <CircleAlert />
      <h1>The result artifact could not be loaded</h1>
      <p>{message}</p>
      <a href="https://github.com/ruimiguelyo/judgeshift-cx">Inspect the repository</a>
    </main>
  )
}

export default function App() {
  const [data, setData] = useState<Results | null>(null)
  const [stress, setStress] = useState<Results | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all(
      ['results.json', 'stress_results.json'].map((artifact) =>
        fetch(`${import.meta.env.BASE_URL}${artifact}`).then((response) => {
          if (!response.ok) throw new Error(`${artifact}: HTTP ${response.status}`)
          return response.json() as Promise<Results>
        }),
      ),
    )
      .then(([baseResults, stressResults]) => {
        setData(baseResults)
        setStress(stressResults)
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : 'Unknown error'),
      )
  }, [])

  useEffect(() => {
    if (!data || !stress) return

    const root = document.documentElement
    const revealTargets = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'))
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    root.classList.add('motion-ready')

    const updateScrollProgress = () => {
      const scrollable = root.scrollHeight - window.innerHeight
      const progress = scrollable > 0 ? window.scrollY / scrollable : 0
      root.style.setProperty('--scroll-progress', String(Math.min(1, Math.max(0, progress))))
    }

    updateScrollProgress()
    window.addEventListener('scroll', updateScrollProgress, { passive: true })

    if (prefersReducedMotion) {
      revealTargets.forEach((element) => element.classList.add('is-visible'))
      return () => window.removeEventListener('scroll', updateScrollProgress)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        })
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 },
    )

    revealTargets.forEach((element) => observer.observe(element))

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', updateScrollProgress)
    }
  }, [data, stress])

  if (error) return <ErrorState message={error} />
  if (!data || !stress) return <LoadingState />

  const sortedStrategies = [...data.strategies].sort(
    (a, b) => strategyOrder.indexOf(a.strategy) - strategyOrder.indexOf(b.strategy),
  )
  const rubric = data.strategies.find((item) => item.strategy === 'rubric')
  const selective = data.strategies.find((item) => item.strategy === 'rubric_selective')
  const abstentions = (selective?.n_total ?? 0) - (selective?.n_answered ?? 0)
  const generated = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(data.metadata.generated_at))

  return (
    <>
      <div className="scroll-progress" aria-hidden="true"><span /></div>
      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="JudgeShift CX home">
          <span className="brand-mark">JS</span>
          <span>JudgeShift <em>CX</em></span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#results">Results</a>
          <a href="#cases">Cases</a>
          <a href="#method">Method</a>
        </nav>
        <a className="github-link" href="https://github.com/ruimiguelyo/judgeshift-cx">
          <Github size={17} />
          GitHub
          <ArrowUpRight size={15} />
        </a>
      </header>

      <main id="top">
        <section className="hero section-shell">
          <div className="hero-copy" data-reveal>
            <div className="eyebrow"><span /> LLM JUDGE EVALUATION / EN + PT-PT</div>
            <h1>I evaluated LLM judges on bilingual support responses.</h1>
            <p className="hero-lead">
              I compared four strategies on 36 cases, reversed every response order, and ran a
              targeted verbosity stress test against documented reference decisions.
            </p>
            <div className="hero-actions">
              <a className="primary-button" href="#results">
                Explore the results <ArrowDown size={17} />
              </a>
              <a className="text-button" href="https://github.com/ruimiguelyo/judgeshift-cx/blob/main/docs/METHODOLOGY.md">
                Read the methodology <ArrowUpRight size={15} />
              </a>
            </div>
          </div>
          <aside className="hero-panel" data-reveal>
            <div className="hero-panel-head">
              <span>Experiment / 001</span>
              <span className="status-dot">Reproducible</span>
            </div>
            <div className="hero-stat-grid">
              <div><strong>{data.dataset.cases}</strong><span>evaluation cases</span></div>
              <div><strong>{data.dataset.paired_scenarios}</strong><span>bilingual pairs</span></div>
              <div><strong>4</strong><span>judge strategies</span></div>
              <div><strong>0</strong><span>remote API calls</span></div>
            </div>
            <div className="model-chip">
              <Sparkles size={17} />
              <div><span>Local open-weight judge</span><strong>{compactModel(data.metadata.model)}</strong></div>
            </div>
            <div className="artifact-line">
              <span>Dataset</span>
              <code>{data.metadata.dataset_sha256.slice(0, 12)}…</code>
            </div>
            <div className="artifact-line">
              <span>Generated</span>
              <code>{generated}</code>
            </div>
          </aside>
        </section>

        <section className="thesis-band" data-reveal aria-label="Experiment characteristics">
          <div><span>Dataset</span><strong>36 cases</strong></div>
          <div><span>Languages</span><strong>EN / PT-PT</strong></div>
          <div><span>Passes</span><strong>A/B + B/A</strong></div>
          <div><span>Inference</span><strong>Local</strong></div>
          <div><span>Artifacts</span><strong>Committed</strong></div>
        </section>

        <section className="section-shell results-section" id="results">
          <div className="section-heading split-heading" data-reveal>
            <div>
              <div className="eyebrow"><span /> BASE BENCHMARK / 36 CASES</div>
              <h2>Strategy performance on the reference set</h2>
            </div>
            <p>
              I report agreement together with coverage, confidence intervals, and order-flip
              rates so abstentions and unstable decisions remain visible.
            </p>
          </div>

          <div className="strategy-grid" data-reveal>
            {sortedStrategies.map((item) => (
              <StrategyCard item={item} featured={item.strategy === 'rubric_selective'} key={item.strategy} />
            ))}
          </div>

          <div className="insight-grid" data-reveal>
            <article className="frontier-card">
              <div className="card-heading">
                <div>
                  <span>Reliability frontier</span>
                  <h3>Agreement vs. coverage</h3>
                </div>
                <MetricInfo label="How to read" text="Upper-right is better: more reference agreement while retaining more cases." />
              </div>
              <ReliabilityChart strategies={sortedStrategies} />
            </article>
            <article className="tradeoff-card">
              <span className="tradeoff-kicker">ORDER-STABILITY FILTER</span>
              <h3>I excluded {abstentions} judgments after the A/B swap.</h3>
              <p>
                Reversing A and B changed the rubric's normalized decision in{' '}
                <strong>{pct(rubric?.order_flip_rate ?? null)}</strong> of comparable cases. The
                selective strategy trades {pct(1 - (selective?.coverage ?? 1))} of coverage for
                {' '}{pct((selective?.agreement ?? 0) - (rubric?.agreement ?? 0), 1)} change in agreement.
              </p>
              <div className="tradeoff-flow">
                <div><span>01</span><strong>Judge A / B</strong></div>
                <ArrowDown size={16} />
                <div><span>02</span><strong>Swap B / A</strong></div>
                <ArrowDown size={16} />
                <div className="flow-highlight"><span>03</span><strong>Agree or abstain</strong></div>
              </div>
              <a href="#cases">Inspect every abstention <ArrowUpRight size={15} /></a>
            </article>
          </div>

          <StressTest base={data} stress={stress} />
        </section>

        <section className="parity-section section-shell">
          <div className="section-heading split-heading compact-heading" data-reveal>
            <div>
              <div className="eyebrow"><span /> LANGUAGE BREAKDOWN</div>
              <h2>Agreement in English and Portuguese</h2>
            </div>
            <p>
              I pair each scenario across both languages so differences remain visible instead of
              being hidden inside one aggregate score.
            </p>
          </div>
          <BreakdownBars data={data} strategy="rubric_selective" />
        </section>

        <section className="cases-section" id="cases">
          <div className="section-shell">
            <div className="section-heading split-heading compact-heading" data-reveal>
              <div>
                <div className="eyebrow light"><span /> CASE-LEVEL EVIDENCE</div>
                <h2>Inspect individual judge decisions</h2>
              </div>
              <p>
                Filter all 36 comparisons, inspect each answer, and see the judge rationale,
                swap-test status, reference reason, and upstream annotation provenance.
              </p>
            </div>
            <CaseExplorer data={data} stress={stress} />
          </div>
        </section>

        <section className="method-section" id="method">
          <div className="section-shell method-shell" data-reveal>
            <header className="method-header">
              <div>
                <div className="eyebrow light"><span /> METHOD / EXECUTION TRACE</div>
                <h2>How I produced each score</h2>
              </div>
              <div className="method-run-meta">
                <div><span>Run mode</span><strong>Local / deterministic</strong></div>
                <div><span>Model</span><strong>{compactModel(data.metadata.model)}</strong></div>
                <div><span>Remote calls</span><strong>0</strong></div>
              </div>
            </header>

            <div className="method-ledger" aria-label="Evaluation execution stages">
              <div className="method-ledger-head" aria-hidden="true">
                <span>Stage</span><span>Operation</span><span>Committed evidence</span>
              </div>
              <article>
                <div className="method-stage"><span>01</span><strong>DATA</strong></div>
                <div><h3>Curate the reference set</h3><p>I combine controlled support scenarios with documented HelpSteer3 preference pairs in English and pt-PT.</p></div>
                <div className="method-evidence"><code>data/golden_dataset.jsonl</code><span>36 cases · 18 pairs</span></div>
              </article>
              <article>
                <div className="method-stage"><span>02</span><strong>INFER</strong></div>
                <div><h3>Judge both response orders</h3><p>I run holistic and rubric prompts with A/B and B/A presentation under greedy local inference.</p></div>
                <div className="method-evidence"><code>artifacts/decisions.jsonl</code><span>216 decisions / experiment</span></div>
              </article>
              <article>
                <div className="method-stage"><span>03</span><strong>NORMALIZE</strong></div>
                <div><h3>Map letters back to response identity</h3><p>I normalize swapped outputs before comparing choices, so a position change cannot appear as agreement.</p></div>
                <div className="method-evidence"><code>original + swapped</code><span>Order flips retained</span></div>
              </article>
              <article>
                <div className="method-stage"><span>04</span><strong>REPORT</strong></div>
                <div><h3>Calculate reliability metrics</h3><p>I report agreement, uncertainty, coverage, kappa, order flips, language consistency, and abstentions.</p></div>
                <div className="method-evidence"><code>artifacts/results.json</code><span>Dashboard input</span></div>
              </article>
            </div>

            <footer className="method-footer">
              <p>Every chart reads committed JSON; raw model output and provenance remain available for inspection.</p>
              <a className="text-button" href="https://github.com/ruimiguelyo/judgeshift-cx/tree/main/docs">
                Open the documentation <ArrowUpRight size={15} />
              </a>
            </footer>
          </div>
        </section>

        <section className="limitations-band">
          <div className="section-shell limitations-inner" data-reveal>
            <CircleAlert size={25} />
            <div>
              <span>STUDY SCOPE</span>
              <h2>36 cases across 18 bilingual scenario pairs</h2>
              <p>
                The translated human preferences are not independent Portuguese annotations;
                policy references reflect documented author judgment; 18 scenario groups produce
                wide intervals; and order stability does not guarantee correctness.
              </p>
            </div>
            <a href="https://github.com/ruimiguelyo/judgeshift-cx/blob/main/docs/LIMITATIONS.md">
              All limitations <ArrowUpRight size={15} />
            </a>
          </div>
        </section>

        <section className="glossary-section" id="glossary">
          <div className="section-shell">
            <div className="glossary-intro" data-reveal>
              <div>
                <div className="eyebrow"><span /> PLAIN-LANGUAGE GLOSSARY</div>
                <h2>Terms used on this page</h2>
              </div>
              <p>Short definitions for the main evaluation terms.</p>
            </div>
            <dl className="glossary-grid" data-reveal>
              {glossaryTerms.map((item) => (
                <div key={item.term}>
                  <dt>{item.term}</dt>
                  <dd>{item.definition}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      <footer className="site-footer section-shell" data-reveal>
        <div className="footer-main">
          <div>
            <a className="wordmark" href="#top"><span className="brand-mark">JS</span><span>JudgeShift <em>CX</em></span></a>
            <p>I built this project to inspect AI-agent evaluation decisions, compare languages, and measure uncertainty explicitly.</p>
          </div>
          <div className="footer-links">
            <section><span>PROJECT</span><a href="#results">Results</a><a href="#cases">Cases</a><a href="#method">Method</a><a href="#glossary">Glossary</a></section>
            <section><span>EVIDENCE</span><a href="https://github.com/ruimiguelyo/judgeshift-cx/blob/main/docs/DATASET_CARD.md">Dataset card</a><a href="https://github.com/ruimiguelyo/judgeshift-cx/blob/main/artifacts/decisions.jsonl">Raw decisions</a><a href="https://github.com/ruimiguelyo/judgeshift-cx/actions">CI checks</a></section>
          </div>
        </div>
        <div className="footer-bottom">
          <span>Built by Rui Sá · 2026</span>
          <span>Code MIT · mixed-provenance dataset</span>
          <a href="https://github.com/ruimiguelyo/judgeshift-cx"><Github size={15} /> Source</a>
        </div>
      </footer>
    </>
  )
}
